import { isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { isCxsCompatibleTelSys } from '../../../NonReact/utils/CxsSubscriptionAccessUtils'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { COMMA } from '../../../NonReact/constants/Separators'
import { ReportingAccessLevels } from '../../AddUser/Reporting/ReportingAccessLevels'
import { TelSysIntegrationMode } from '../Types/Types'
import {
    BUSINESS_OPTION,
    BusinessOption,
    INTEGRATION_TYPE,
    IntegrationType
} from '../../TelSysConfiguration/Constants/Constants'

/**
 * Parses a telsys {@code IntegrationModeOptions} value (comma-separated, e.g.
 * {@code 'CX-Analytics,CX-Score'}) into business integration options. Returns {@code undefined}
 * when the value is absent so callers can distinguish "not loaded" from "none enabled".
 *
 * @param integrationModeOptions Raw comma-separated options from the telsys, when known.
 * @returns The parsed options, or {@code undefined} when the value is absent.
 */
export function parseBusinessIntegrationOptions(
    integrationModeOptions: Nullable<string>
): Definable<readonly BusinessOption[]> {
    if (!isStringNotEmpty(integrationModeOptions)) return undefined

    return integrationModeOptions.split(COMMA) as BusinessOption[]
}

/**
 * Whether the telsys has a specific business integration option enabled. Unknown options
 * (not yet loaded) count as not enabled.
 *
 * @param businessIntegrationOptions Parsed telsys business integration options, when known.
 * @param option The business option to look for.
 * @returns {@code true} when the option is present.
 */
export function hasBusinessIntegrationOption(
    businessIntegrationOptions: Definable<readonly BusinessOption[]>,
    option: BusinessOption
): boolean {
    return businessIntegrationOptions?.includes(option) ?? false
}

/**
 * Whether CX Score provisioning applies for a telsys. The single source of truth shared by the
 * Add/Modify User modal, Users by Extension and the Licence Management page so they cannot
 * drift. CX Score is only offered for MS Teams and BroadWorks (APD-13344):
 * - MS Teams: requires the {@code CX-Score} business integration option (only Business
 *   real-time telsys carry it).
 * - BroadWorks (real, or a Simulator emulating BroadWorks): requires an Enterprise partition
 *   licence; unknown licences (still loading / not provided) keep CXS available rather than
 *   flickering it away.
 * - Any other known telsys (e.g. Webex Calling): never eligible.
 * - An unknown / not-yet-selected telsys ({@code null}, {@code undefined} or
 *   {@link TelSysTypes.UNKNOWN}) keeps CXS available, matching the unknown-licence handling so the
 *   option is not flickered away while the telsys is still loading or in the all-telsys summary.
 *
 * Feature-flag and scope checks remain the caller's responsibility.
 *
 * @param telSysType Telephony server type.
 * @param businessIntegrationOptions Parsed telsys business integration options, when known.
 * @param partitionLicences Reporting licences held by the telsys partitions, when known.
 * @param callControlOptions The telsys {@code CallControlOptions} value; only relevant for
 *        distinguishing a Simulator emulating BroadWorks.
 * @returns {@code true} when CX Score applies for the telsys.
 */
export function isCxsProductApplicable(
    telSysType: Nullable<number | string>,
    businessIntegrationOptions: Definable<readonly BusinessOption[]>,
    partitionLicences: Nullable<readonly string[]>,
    callControlOptions?: Nullable<string>
): boolean {
    if (telSysType === TelSysTypes.MS_TEAMS) {
        return hasBusinessIntegrationOption(businessIntegrationOptions, BUSINESS_OPTION.CX_SCORE)
    }

    // A known telsys that is neither MS Teams nor BroadWorks-compatible (e.g. Webex Calling) never
    // offers CX Score. An unknown / not-yet-loaded telsys falls through to the licence check.
    const isKnownTelSys: boolean = telSysType != null && telSysType !== TelSysTypes.UNKNOWN

    if (isKnownTelSys && !isCxsCompatibleTelSys(telSysType as Nullable<TelSysTypes>, callControlOptions ?? undefined)) {
        return false
    }

    return !Array.isArray(partitionLicences) || partitionLicences.includes(ReportingAccessLevels.ENTERPRISE)
}

/**
 * Whether CX Analytics provisioning applies for a telsys. Mirrors the backend rule in
 * {@code CReqRespTelSysUtils.checkAndValidateTelSysConditionsForPresencePage}, so the licence
 * surfaces cannot drift from it:
 * - A telsys in CXI-Only integration mode carries no CX Analytics reporting at all, whatever the
 *   telsys type, so CXA never applies (APD-13472). Legacy Teams, BroadWorks and Webex Calling can
 *   all be configured this way.
 * - Legacy Teams: CXA always applies. Legacy telsys carry no business integration options — the
 *   telsys modal only offers them for Business, and the backend only reads them for Business
 *   real-time — so there is no option to gate on.
 * - Business Teams: requires the {@code CX-Analytics} business integration option, or Historic
 *   mode, where the telsys config clears every option but Historic User / Manager remain
 *   assignable (APD-13426).
 * - Any non-Teams telsys: CXA applies, subject only to the CXI-Only check above.
 *
 * @param telSysType Telephony server type.
 * @param integrationType MS Teams integration type ({@code 'legacy'} / {@code 'business'}), when known.
 * @param integrationMode The telsys integration mode, when known.
 * @param businessIntegrationOptions Parsed telsys business integration options, when known.
 * @param isTelSysHistoricOnly Whether the telsys runs in Historic mode, or null when unknown.
 * @returns {@code true} when CX Analytics applies for the telsys.
 */
export function isCxaProductApplicable(
    telSysType: Nullable<number | string>,
    integrationType: Nullable<IntegrationType>,
    integrationMode: Nullable<TelSysIntegrationMode>,
    businessIntegrationOptions: Definable<readonly BusinessOption[]>,
    isTelSysHistoricOnly: Nullable<boolean>
): boolean {
    if (integrationMode === TelSysIntegrationMode.CXI_ONLY) return false

    if (telSysType !== TelSysTypes.MS_TEAMS) return true

    if (integrationType === INTEGRATION_TYPE.LEGACY_TEAMS) return true

    const isBusinessTeams: boolean = integrationType === INTEGRATION_TYPE.BUSINESS

    return (
        hasBusinessIntegrationOption(businessIntegrationOptions, BUSINESS_OPTION.CX_ANALYTICS) ||
        (isBusinessTeams && (isTelSysHistoricOnly ?? false))
    )
}

/**
 * Whether CX Record provisioning applies for a telsys: it requires the {@code CX-Record}
 * business integration option (MS Teams Business only — Legacy and non-Teams telsys never
 * carry it).
 *
 * @param businessIntegrationOptions Parsed telsys business integration options, when known.
 * @returns {@code true} when CX Record applies for the telsys.
 */
export function isCxrProductApplicable(businessIntegrationOptions: Definable<readonly BusinessOption[]>): boolean {
    return hasBusinessIntegrationOption(businessIntegrationOptions, BUSINESS_OPTION.CX_RECORD)
}

/**
 * Whether CX Integration applies for a telsys: Legacy Teams always offers it, Business Teams
 * requires the {@code CX-Integration} business integration option. Feature-flag and CME
 * capability checks remain the caller's responsibility.
 *
 * @param telSysType Telephony server type.
 * @param integrationType MS Teams integration type ({@code 'legacy'} / {@code 'business'}), when known.
 * @param businessIntegrationOptions Parsed telsys business integration options, when known.
 * @returns {@code true} when CX Integration applies for the telsys.
 */
export function isCxiProductApplicable(
    telSysType: Nullable<number | string>,
    integrationType: Nullable<IntegrationType>,
    businessIntegrationOptions: Definable<readonly BusinessOption[]>
): boolean {
    const isLegacyTeams: boolean =
        telSysType === TelSysTypes.MS_TEAMS && integrationType === INTEGRATION_TYPE.LEGACY_TEAMS

    return isLegacyTeams || hasBusinessIntegrationOption(businessIntegrationOptions, BUSINESS_OPTION.CX_INTEGRATION)
}

/**
 * Whether the telsys modal should offer the {@code CX-Score} business integration option. The
 * option is gated behind the CX Score feature flag, but stays visible on a telsys that already
 * carries it so an existing selection is not silently dropped while the flag is off.
 *
 * @param cxScoreFeatureEnabled Resolved {@code enableCXScore} feature flag value.
 * @param currentIntegrationModeOptions The telsys's current raw {@code IntegrationModeOptions}, when editing.
 * @returns {@code true} when the CX-Score option should be offered.
 */
export function shouldOfferCxScoreBusinessOption(
    cxScoreFeatureEnabled: boolean,
    currentIntegrationModeOptions: Nullable<string>
): boolean {
    return (
        cxScoreFeatureEnabled ||
        hasBusinessIntegrationOption(
            parseBusinessIntegrationOptions(currentIntegrationModeOptions),
            BUSINESS_OPTION.CX_SCORE
        )
    )
}
